// Corpus-free category definitions — safe for client bundles.
export type WpFaqCategory =
  | "basics"
  | "employer-conditions"
  | "forms-process"
  | "visa-link"
  | "boi-onestop"
  | "restricted-jobs"
  | "documents"
  | "renewal-change"
  | "compliance-penalty"
  | "special-routes";

export interface WpCategoryDef {
  slug: WpFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const WP_FAQ_CATEGORIES: WpCategoryDef[] = [
  {
    slug: "basics",
    titleTh: "พื้นฐานใบอนุญาตทำงาน — ใครต้องมี และ “ทำงาน” หมายถึงอะไร",
    titleEn: "Work Permit Basics — Who Needs One and What Counts as Work",
    descTh:
      "นิยามการทำงานของคนต่างชาติตามพระราชกำหนดการบริหารจัดการการทำงานของคนต่างด้าว ใครได้รับยกเว้น และใบอนุญาตทำงานผูกกับอะไร",
    descEn:
      "How Thai law defines foreign work, who is exempt, and what a work permit is actually tied to.",
    matchTh: /(ใครต้องมีใบอนุญาต|นิยามการทำงาน|ยกเว้น|ใบอนุญาตทำงานคือ|ผูกกับนายจ้าง|ทำงานโดยไม่มีใบอนุญาต)/i,
    matchEn: /(who needs a work permit|definition of work|exempt|what is a work permit|tied to the employer)/i,
  },
  {
    slug: "employer-conditions",
    titleTh: "เงื่อนไขนายจ้าง — ทุนจดทะเบียน อัตราส่วนลูกจ้างไทย และเอกสารบริษัท",
    titleEn: "Employer Conditions — Capital, Thai-Staff Ratio, Company Records",
    descTh:
      "เกณฑ์ทุนจดทะเบียนชำระแล้วต่อคนต่างชาติหนึ่งคน อัตราส่วนลูกจ้างไทย งบการเงิน ภาษี และประกันสังคมที่เจ้าหน้าที่ตรวจ",
    descEn:
      "Paid-up capital per foreign employee, the Thai-staff ratio, and the financial, tax and social-security records officials check.",
    matchTh: /(ทุนจดทะเบียน|2 ล้าน|ลูกจ้างไทย|อัตราส่วน|ประกันสังคม|งบการเงิน|ภ\.?ง\.?ด|ภ\.?พ\.?30)/i,
    matchEn: /(paid-?up capital|2 million|thai (staff|employee)|ratio|social security|financial statement|withholding)/i,
  },
  {
    slug: "forms-process",
    titleTh: "แบบฟอร์มและขั้นตอน — ตท. / WP และการยื่นที่กรมการจัดหางาน",
    titleEn: "Forms & Process — WP/BT Forms and Filing at the Department of Employment",
    descTh: "ลำดับการยื่นคำขอ แบบฟอร์มที่ใช้ ระยะเวลาพิจารณา และการยื่นผ่านระบบออนไลน์ของกรมการจัดหางาน",
    descEn: "Filing order, the forms used, review timelines, and the Department of Employment e-service.",
    matchTh: /(ตท\.|WP\.?3|WP\.?1|แบบฟอร์ม|ขั้นตอนการยื่น|กรมการจัดหางาน|สำนักงานจัดหางานจังหวัด|e-?workpermit|ระยะเวลาพิจารณา)/i,
    matchEn: /(WP\.?3|WP\.?1|form|filing step|department of employment|provincial employment office|processing time)/i,
  },
  {
    slug: "visa-link",
    titleTh: "ความสัมพันธ์กับวีซ่า Non-B และการขออยู่ต่อ 1 ปี",
    titleEn: "How It Links to the Non-B Visa and the 1-Year Extension",
    descTh: "ทำไมต้องมีวีซ่าที่ถูกต้องก่อน การเปลี่ยนประเภทการตรวจลงตราในไทย และการยื่น ตม.7 ขออยู่ต่อ 1 ปี",
    descEn: "Why a correct visa comes first, changing visa category inside Thailand, and filing TM.7 for a one-year extension.",
    matchTh: /(Non-?B|วีซ่าธุรกิจ|เปลี่ยนประเภทการตรวจลงตรา|ตม\.7|อยู่ต่อ 1 ปี|re-?entry)/i,
    matchEn: /(Non-?B|business visa|change of visa|TM\.?7|one-?year extension|re-?entry)/i,
  },
  {
    slug: "boi-onestop",
    titleTh: "BOI และศูนย์บริการวีซ่าและใบอนุญาตทำงาน (One Stop Service)",
    titleEn: "BOI and the One Stop Service Centre",
    descTh: "สิทธิประโยชน์ด้านช่างฝีมือของกิจการที่ได้รับการส่งเสริม ระบบ e-Expert และการดำเนินการที่ศูนย์ OSS",
    descEn: "Skilled-worker privileges for promoted companies, the e-Expert system, and processing at the One Stop Service Centre.",
    matchTh: /(BOI|ส่งเสริมการลงทุน|One Stop|e-?Expert|ช่างฝีมือ|ผู้ชำนาญการ|IEAT|นิคมอุตสาหกรรม)/i,
    matchEn: /(BOI|one stop|e-?Expert|skilled worker|specialist|IEAT|industrial estate)/i,
  },
  {
    slug: "restricted-jobs",
    titleTh: "อาชีพที่ห้ามและอาชีพที่มีเงื่อนไขสำหรับคนต่างชาติ",
    titleEn: "Occupations Prohibited or Conditionally Open to Foreigners",
    descTh: "บัญชีงานที่ห้ามคนต่างชาติทำและงานที่ทำได้ตามเงื่อนไข ตามประกาศกระทรวงแรงงาน และผลต่อการกำหนดตำแหน่งงาน",
    descEn: "The list of occupations closed or conditionally open to foreigners under Ministry of Labour notifications, and how it shapes job titles.",
    matchTh: /(อาชีพที่ห้าม|งานที่ห้าม|39 อาชีพ|สงวนไว้|ตำแหน่งงาน|ไกด์|มัคคุเทศก์|ตัดผม|ขายของหน้าร้าน)/i,
    matchEn: /(prohibited occupation|reserved for thai|restricted job|job title|tour guide|hairdress|street vend)/i,
  },
  {
    slug: "documents",
    titleTh: "เอกสาร คำแปล และการรับรองที่ต้องเตรียม",
    titleEn: "Documents, Translation & Legalization",
    descTh: "วุฒิการศึกษาและหนังสือรับรองประสบการณ์จากต่างประเทศที่ต้องแปลและรับรอง ใบรับรองแพทย์ และรูปถ่าย",
    descEn: "Foreign degrees and experience letters that must be translated and legalized, medical certificates, and photographs.",
    matchTh: /(วุฒิการศึกษา|ปริญญา|transcript|หนังสือรับรองประสบการณ์|ใบรับรองแพทย์|รูปถ่าย|แปลเอกสาร|รับรองคำแปล|apostille|กงสุล)/i,
    matchEn: /(degree|diploma|transcript|experience letter|medical certificate|photograph|translat|legaliz|apostille)/i,
  },
  {
    slug: "renewal-change",
    titleTh: "ต่ออายุ เปลี่ยนนายจ้าง เปลี่ยนตำแหน่ง และเพิ่มสถานที่ทำงาน",
    titleEn: "Renewal, Changing Employer, Job Title or Workplace",
    descTh: "กรอบเวลาการต่ออายุ การแจ้งเปลี่ยนแปลงรายการในใบอนุญาต การลาออก และช่วงเวลาที่กฎหมายให้หานายจ้างใหม่",
    descEn: "Renewal windows, notifying changes on the permit, resignation, and the grace period to find a new employer.",
    matchTh: /(ต่ออายุ|เปลี่ยนนายจ้าง|เปลี่ยนตำแหน่ง|เพิ่มสถานที่|ลาออก|ยกเลิกใบอนุญาต|แจ้งออกจากงาน|15 วัน|30 วัน)/i,
    matchEn: /(renew|change (of )?employer|change (of )?position|add(ing)? workplace|resign|cancel(lation)?|notify)/i,
  },
  {
    slug: "compliance-penalty",
    titleTh: "หน้าที่ต่อเนื่องและบทลงโทษ",
    titleEn: "Ongoing Obligations and Penalties",
    descTh: "โทษของการทำงานโดยไม่มีใบอนุญาตและของนายจ้าง การพาใบอนุญาตติดตัว การตรวจสถานประกอบการ และหน้าที่ ตม.30 / 90 วัน",
    descEn: "Penalties for unlicensed work and for employers, carrying the permit, workplace inspections, and TM.30 / 90-day duties.",
    matchTh: /(บทลงโทษ|ปรับ|ผิดกฎหมาย|ตรวจสถานประกอบการ|พาใบอนุญาต|ตม\.30|90 วัน|ภาษีเงินได้บุคคลธรรมดา)/i,
    matchEn: /(penalt|fine|illegal|inspection|carry the permit|TM\.?30|90-?day|personal income tax)/i,
  },
  {
    slug: "special-routes",
    titleTh: "ทางเลือกพิเศษ — SMART Visa, LTR, DTV และครูต่างชาติ",
    titleEn: "Special Routes — SMART Visa, LTR, DTV and Foreign Teachers",
    descTh: "กรณีที่ไม่ต้องมีใบอนุญาตทำงานแบบเดิม หรือมีเงื่อนไขเฉพาะ เช่น ครูต่างชาติ นักกีฬา และผู้แทนสำนักงาน",
    descEn: "Cases that replace the standard permit or carry special conditions — teachers, athletes, representative offices.",
    matchTh: /(SMART|LTR|DTV|Digital Nomad|ครูต่างชาติ|ใบอนุญาตประกอบวิชาชีพครู|สำนักงานผู้แทน|นักกีฬา|freelance|ทำงานทางไกล)/i,
    matchEn: /(SMART|LTR|DTV|digital nomad|teacher|teaching licen|representative office|athlete|freelance|remote work)/i,
  },
];

export function getWpCategoryDef(slug: string): WpCategoryDef | undefined {
  return WP_FAQ_CATEGORIES.find((c) => c.slug === slug);
}